import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

interface BaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string | React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  /** When set, the dialog occupies a fixed height instead of sizing to content */
  height?: string;
  showCloseButton?: boolean;
  /** Padding applied to the body area. Default '24px'. Pass '0' for edge-to-edge content. */
  contentPadding?: string;
  contentStyle?: React.CSSProperties;
  /** Optional content rendered between the header and body (e.g., banners, error notices) */
  headerContent?: React.ReactNode;
}

const useStyles = makeStyles({
  dialogSurface: {
    width: '100%',
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    // Below 768px, clamp the dialog to the viewport so the caller's
    // maxWidth (e.g. 900px) doesn't push the surface offscreen and so
    // the actions footer stays reachable on a 375px-wide phone. The
    // inline style on DialogSurface still wins for desktop because
    // !important is only applied inside this @media block.
    '@media (max-width: 768px)': {
      maxWidth: '100vw !important',
      width: '100vw !important',
      maxHeight: '100vh !important',
      height: '100vh !important',
      ...shorthands.borderRadius('0'),
    },
  },
  dialogContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 0,
    maxHeight: 'inherit',
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('12px'),
    ...shorthands.padding('16px', '24px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    flexShrink: 0,
  },
  dialogTitle: {
    margin: 0,
    paddingBottom: 0,
  },
  dialogBody: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  dialogFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    ...shorthands.gap('8px'),
    ...shorthands.padding('16px', '24px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    flexShrink: 0,
    backgroundColor: 'transparent',
  },
});

/**
 * BaseDialog - Reusable dialog wrapper component
 *
 * Three-row layout (header / body / footer) inside a padding-free DialogSurface,
 * so the header divider, footer divider, and any banners span the full modal width.
 *
 * Usage:
 * ```tsx
 * <BaseDialog
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="My Dialog"
 *   actions={
 *     <>
 *       <Button onClick={onClose}>Cancel</Button>
 *       <Button appearance="primary" onClick={handleSave}>Save</Button>
 *     </>
 *   }
 * >
 *   <div>Dialog content goes here</div>
 * </BaseDialog>
 * ```
 */
export const BaseDialog: React.FC<BaseDialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  actions,
  width,
  maxWidth = '900px',
  maxHeight = '90vh',
  height,
  showCloseButton = false,
  contentPadding = '24px',
  contentStyle = {},
  headerContent,
}) => {
  const styles = useStyles();

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface
        className={styles.dialogSurface}
        style={{ width, maxWidth, maxHeight, height }}
      >
        <div className={styles.dialogContainer} style={{ height }}>
          <div className={styles.dialogHeader}>
            <DialogTitle className={styles.dialogTitle}>{title}</DialogTitle>
            {showCloseButton && (
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            )}
          </div>
          {headerContent}
          <div
            className={styles.dialogBody}
            style={{ padding: contentPadding, ...contentStyle }}
          >
            {children}
          </div>
          {actions && <div className={styles.dialogFooter}>{actions}</div>}
        </div>
      </DialogSurface>
    </Dialog>
  );
};

export default BaseDialog;
